import {
    isPaidListing as isPaidListingShared,
    isPaidWithCheckout as isPaidWithCheckoutShared,
} from '~/modules/utilities/pricing_utils';
import { handleException } from '~/modules/utilities/error_utilities';

const PLAN_CUSTOM_UNITS = 'custom';
const PRICING_MODEL_TYPE_FREE = 'free';
const PRICING_MODEL_TYPE_FREEMIUM = 'freemium';
const PRICING_MODEL_TYPE_FREETOINSTSALL = 'free_to_install';
const PRICING_MODELS_FREE = [PRICING_MODEL_TYPE_FREE, PRICING_MODEL_TYPE_FREEMIUM, PRICING_MODEL_TYPE_FREETOINSTSALL];

// Shared with v1's pricing.ts (which keeps its own copy since it's frozen).
// The `{12}` alternative already matches ids of 15+ chars (unanchored `.test()`),
// so the `{15}` alternative from the original v1 regex is redundant and dropped here.
export const SFID_REGEX = new RegExp('^(a|A)0(y|Y)[a-zA-Z0-9]{12}');

export const isPaidListing = (pricing: schema.oaf.Pricing): boolean => isPaidListingShared(pricing);

export const isFreeListing = (pricing: schema.oaf.Pricing): boolean => {
    return pricing?.price_model_type === PRICING_MODEL_TYPE_FREE;
};
export const isFreemiumListing = (pricing: schema.oaf.Pricing): boolean => {
    return pricing?.price_model_type === PRICING_MODEL_TYPE_FREEMIUM;
};
export const isFreeToInstallListing = (pricing: schema.oaf.Pricing): boolean => {
    return pricing?.price_model_type === PRICING_MODEL_TYPE_FREETOINSTSALL;
};
/*
 * TODO (W-11390239) We are temporarily going to normalize the 3 types of "free" listings and display them all just as "free".
 * In the future, we will be supporting 3 different types of free listings and this code will then need to be changed
 */
export const isFreeListingPreTransparency = (pricing: schema.oaf.Pricing): boolean => {
    return PRICING_MODELS_FREE.includes(pricing?.price_model_type);
};

export const getPlanUnits = (plan: schema.oaf.SubscriptionPricePlan): string => {
    return (plan?.units === PLAN_CUSTOM_UNITS ? plan.custom_unit : plan?.units) ?? '';
};

export const isLowestStartingPrice = (plan: schema.oaf.SubscriptionPricePlan): boolean => {
    return Boolean(plan?.lowest_starting_price);
};

export const isPaidWithCheckout = (pricing: schema.oaf.Pricing): boolean => isPaidWithCheckoutShared(pricing);

export const isPricingEligibleForRequestPrivateOffer = (pricing?: schema.oaf.Pricing | null): boolean => {
    if (!pricing) {
        return false;
    }
    return !isFreeListing(pricing) && !isPaidWithCheckout(pricing);
};

export const hasDiscountNonProfits = (pricing: schema.oaf.Pricing): boolean => {
    // @ts-expect-error * Automated Type Error Fix
    const pricingModel: schema.oaf.PricingModel = pricing?.model;
    if (isPaidListing(pricing)) {
        // @ts-expect-error * Automated Type Error Fix
        return (pricingModel as schema.oaf.Paid).discounts_offered;
    } else if (isFreeToInstallListing(pricing)) {
        // @ts-expect-error * Automated Type Error Fix
        return (pricingModel as schema.oaf.FreeToInstall).discounts_offered;
    } else if (isFreemiumListing(pricing)) {
        // @ts-expect-error * Automated Type Error Fix
        return (pricingModel as schema.oaf.Freemium).discounts_offered;
    }
    return false;
};
//adding some comment to check verified commits
export const getPlanPrice = (currency: string, price: number): string => {
    // `price != null` (not truthy) so a legitimate 0-priced plan formats to "$0" rather than blank.
    if (currency && price != null) {
        try {
            // TODO - I18N: This formatting does not take into account the current language
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency,
                unitDisplay: 'short',
                currencyDisplay: 'narrowSymbol',
                signDisplay: 'never',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(price);
        } catch (error) {
            if (!(error instanceof RangeError)) {
                throw error;
            }
            // Anticipated: invalid ISO currency code throws RangeError; log and fall through to empty string.
            handleException(error, 'pricingHelper.getPlanPrice', {
                level: 'warn',
                debugData: { currency },
            });
        }
    }
    // if we don't get necessary data to format the price we just return empty string
    return '';
};
